import { createRouter, createWebHistory } from 'vue-router'
import { Auth } from '@aws-amplify/auth'
import HomeView from '../views/HomeView.vue'
import LessonView from '../views/LessonView.vue'
import AdminView from '../views/AdminView.vue'
import Login from '../components/Login.vue'
import Callback from '../components/Callback.vue'
import Signup from '../components/Signup.vue'
import { checkAdminStatus } from '../services/adminAuth'
import { checkSignupStatus } from '../services/userService'

// Helper function to check authentication
const checkAuth = async () => {
  try {
    await Auth.currentAuthenticatedUser()
    return true
  } catch {
    return false
  }
}

// Helper function to check if user has completed signup
const checkUserSignup = async () => {
  try {
    const response = await checkSignupStatus()
    return response.signupComplete
  } catch {
    return false
  }
}

// Helper function to handle root domain redirect
const handleRootDomainRedirect = () => {
  const hostname = window.location.hostname
  if (hostname === 'codeshipwatch.com') {
    window.location.href = `https://app.codeshipwatch.com${window.location.pathname}`
    return true
  }
  return false
}

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/',
      name: 'home',
      component: HomeView,
      beforeEnter: async (to, from, next) => {
        // First check if we need to redirect to app subdomain
        if (handleRootDomainRedirect()) return

        const isAuthenticated = await checkAuth()
        if (isAuthenticated) {
          // Check if user has completed signup
          const hasCompletedSignup = await checkUserSignup()
          if (hasCompletedSignup) {
            next()
          } else {
            next('/signup')
          }
        } else {
          next('/login')
        }
      }
    },
    {
      path: '/login',
      name: 'login',
      component: Login,
      beforeEnter: async (to, from, next) => {
        // First check if we need to redirect to app subdomain
        if (handleRootDomainRedirect()) return

        const isAuthenticated = await checkAuth()
        if (isAuthenticated) {
          // Check if user has completed signup
          const hasCompletedSignup = await checkUserSignup()
          if (hasCompletedSignup) {
            next('/')
          } else {
            next('/signup')
          }
        } else {
          next()
        }
      }
    },
    {
      path: '/signup',
      name: 'signup',
      component: Signup,
      meta: { requiresAuth: true },
      beforeEnter: async (to, from, next) => {
        // First check if we need to redirect to app subdomain
        if (handleRootDomainRedirect()) return

        const isAuthenticated = await checkAuth()
        if (isAuthenticated) {
          next()
        } else {
          next('/login')
        }
      }
    },
    {
      path: '/callback',
      name: 'callback',
      component: Callback
    },
    {
      path: '/book/:bookId/chapter/:chapterId/lesson/:lessonId',
      name: 'lesson',
      component: LessonView,
      meta: { requiresAuth: true, requiresSignup: true }
    },
    {
      path: '/admin',
      name: 'admin',
      component: AdminView,
      meta: { requiresAuth: true, requiresAdmin: true, requiresSignup: true },
      beforeEnter: async (to, from, next) => {
        // First check if we need to redirect to app subdomain
        if (handleRootDomainRedirect()) return

        // First check if user is authenticated
        const isAuthenticated = await checkAuth()
        if (!isAuthenticated) {
          next('/login')
          return
        }
        
        // Check if user has completed signup
        const hasCompletedSignup = await checkUserSignup()
        if (!hasCompletedSignup) {
          next('/signup')
          return
        }
        
        // Then check if user is admin
        const isAdmin = await checkAdminStatus()
        if (isAdmin) {
          next()
        } else {
          // Redirect non-admin users to home page
          next('/')
        }
      }
    }
  ]
})

router.beforeEach(async (to, from, next) => {
  // First check if we need to redirect to app subdomain
  if (handleRootDomainRedirect()) return

  if (to.matched.some(record => record.meta.requiresAuth)) {
    const isAuthenticated = await checkAuth()
    if (isAuthenticated) {
      // Check if route requires signup
      if (to.matched.some(record => record.meta.requiresSignup)) {
        const hasCompletedSignup = await checkUserSignup()
        if (!hasCompletedSignup) {
          next('/signup')
          return
        }
      }
      
      // Check if route requires admin privileges
      if (to.matched.some(record => record.meta.requiresAdmin)) {
        const isAdmin = await checkAdminStatus()
        if (isAdmin) {
          next()
        } else {
          next('/') // Redirect non-admin users
        }
      } else {
        next() // Regular authenticated route
      }
    } else {
      next('/login')
    }
  } else {
    next()
  }
})

export default router 